import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  AppBar, Box, Toolbar, IconButton, Typography, Menu, Container, Avatar,
  Button, MenuItem, Switch, Snackbar, Fade, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, Backdrop, CircularProgress,
  Stack, Alert
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useThemeContext } from '../contexts/ThemeContext.tsx';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { styled } from "@mui/system";
import LLLogo from "../assets/images/LL2.png";
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

const DEFAULT_AVATAR = "https://api.dicebear.com/7.x/identicon/svg?seed=default";

// --- ÚJ STÍLUSOK A DIALÓGUSHOZ ---

const AnimatedDialog = styled(Dialog, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
    '& .MuiPaper-root': {
        borderRadius: '20px',
        padding: '10px',
        border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(37, 55, 156, 0.2)',
        background: darkMode 
            ? 'linear-gradient(135deg, #000000 0%, #3a0606 100%)' 
            : 'linear-gradient(135deg, #25379c 0%, #1a2a80 100%)',
        color: 'white',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.8)',
    },
    '& .MuiDialogTitle-root': {
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.5rem',
        paddingTop: '20px'
    }
}));

const DialogActionButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'darkMode' && prop !== 'isLogout',
})<{ darkMode: boolean; isLogout?: boolean }>(({ darkMode, isLogout }) => ({
    borderRadius: '12px',
    padding: '10px 24px',
    textTransform: 'none',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    backgroundColor: isLogout 
        ? (darkMode ? '#ff1744' : '#d32f2f') 
        : 'rgba(255,255,255,0.1)',
    color: 'white',
    '&:hover': {
        backgroundColor: isLogout 
            ? (darkMode ? '#b2102f' : '#9a0007') 
            : 'rgba(255,255,255,0.2)',
        transform: 'translateY(-2px)',
        boxShadow: isLogout ? '0 4px 12px rgba(255,0,0,0.4)' : 'none'
    }
}));

// --- NAVBAR STÍLUSOK ---

const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes shineActiveDark {
  0% { box-shadow: 0 0 5px #ff4081; }
  50% { box-shadow: 0 0 15px #ff80ab, 0 0 20px #ff4081; }
  100% { box-shadow: 0 0 5px #ff4081; }
}
@keyframes shineActiveLight {
  0% { box-shadow: 0 0 5px #FFD700; }
  50% { box-shadow: 0 0 15px #FFFACD, 0 0 20px #FFD700; }
  100% { box-shadow: 0 0 5px #FFD700; }
}
`;

const ThemeToggle = styled(Switch)(({ theme }) => ({
    width: 62, height: 36, padding: 7,
    "& .MuiSwitch-switchBase": {
        margin: 9, padding: 0,
        transform: "translateX(0px)",
        transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        "&.Mui-checked": {
            transform: "translateX(28px)",
            color: "#fff",
            "& .MuiSwitch-thumb": { backgroundColor: "#2c3e50" },
            "& + .MuiSwitch-track": { opacity: 1, background: "linear-gradient(to right, #2c3e50, #3498db)" },
        },
        "& .MuiSwitch-thumb": { backgroundColor: "#FFD700" },
    },
    "& .MuiSwitch-track": { borderRadius: 11, opacity: 1, background: "linear-gradient(to right, #ffe603fd, #8a8701ff)" },
}));

const AnimatedAppBar = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
    transition: "all 0.35s ease",
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    zIndex: 1100,
    boxShadow: darkMode ? '0px 4px 15px rgba(255, 0, 0, 0.15)' : '0px 4px 15px rgba(33, 203, 243, 0.2)',
}));

function DashBoardNavBar() {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElApps, setAnchorElApps] = useState<null | HTMLElement>(null);
  const [anchorElWebsites, setAnchorElWebsites] = useState<null | HTMLElement>(null); 
  const [anchorElLeaderBoards, setAnchorElLeaderBoards] = useState<null | HTMLElement>(null); 
  const [anchorElGames, setAnchorElGames] = useState<null | HTMLElement>(null); 
  const [anchorElAdminSites, setAnchorElAdminSites] = useState<null | HTMLElement>(null); 
  
  const { darkMode, toggleDarkMode } = useThemeContext();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [notification, setNotification] = useState<{ type: 'info'; message: string } | null>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);

  // Cookie és Token logika változatlan marad...
  useEffect(() => {
    const token = Cookies.get('token');
    if (token) {
      try {
        const decoded = jwtDecode<{exp: number}>(token);
        if (decoded.exp * 1000 < Date.now()) {
          Cookies.remove('token'); navigate('/login');
        }
      } catch (error) { navigate('/login'); }
    }
  }, [navigate]);

  useEffect(() => {
    const role = Cookies.get('role');
    const roles = Cookies.get('roles');
    if (roles) {
      const rolesArray = roles.split(',');
      setIsAdmin(role === 'ADMIN' || rolesArray.slice(-5).includes('ADMIN'));
    } else {
      setIsAdmin(role === 'ADMIN');
    }
  }, []);

  useEffect(() => {
    const fetchAvatar = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/getavatar`, {
          headers: { Authorization: `Bearer ${Cookies.get('token')}` },
        });
        if (response.ok) {
          const data = await response.json();
          setAvatarUrl(data.avatarUrl);
        }
      } catch (error) { setAvatarUrl(DEFAULT_AVATAR); }
    };
    fetchAvatar();
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const isGamesActive = () => ['/click', '/aimtester', '/caseopening', '/catchme', '/leaderboard', '/aimleaderboard'].some(p => location.pathname === p);
  const isAppsActive = () => ['/encryption', '/randomizer', '/pictureconverter', '/valentincard', '/freewebsites', '/freetrialwebsites', '/paidwebsites'].some(p => location.pathname === p);

  const getButtonStyle = (path: string, specialCondition?: boolean) => {
    const active = specialCondition || isActive(path);
    const activeColor = darkMode ? '#ff4081' : '#FFD700';
    const activeBg = darkMode ? 'rgba(255, 64, 129, 0.25)' : 'rgba(255, 215, 0, 0.25)';
    const animationName = darkMode ? 'shineActiveDark' : 'shineActiveLight';

    return {
        my: 1.8, color: 'white', mx: 0.5, px: 2,
        borderRadius: '20px', textTransform: 'none', fontSize: '0.9rem',
        fontWeight: active ? 'bold' : 'normal',
        transition: 'all 0.3s ease',
        animation: active ? `${animationName} 2s infinite alternate` : 'none',
        backgroundColor: active ? activeBg : 'transparent',
        borderBottom: active ? `2px solid ${activeColor}` : '2px solid transparent',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)', transform: 'translateY(-1px)' }
    };
  };

  const dynamicMenuStyle = {
    elevation: 8,
    sx: {
        overflow: 'visible', mt: 1.5, borderRadius: '12px',
        border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(255, 215, 0, 0.3)',
        background: darkMode 
            ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
            : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
        backgroundSize: '200% 200%',
        animation: 'backgroundMove 12s ease infinite',
        '& .MuiMenuItem-root': {
            borderRadius: '8px', margin: '4px 8px', color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
        },
    },
  };

  const confirmLogout = async () => {
    setLogoutDialogOpen(false);
    setBackdropOpen(true);
    setTimeout(async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/logout`, { method: 'POST', credentials: 'include' });
        if (response.ok) {
          ['role', 'roles', 'token'].forEach(c => Cookies.remove(c));
          localStorage.clear();
          navigate('/');
        }
      } catch (error) { console.error(error); }
      setBackdropOpen(false);
    }, 2000);
  };

  return (
    <>
      <style>{keyframes}</style>
      <AnimatedAppBar position="fixed" darkMode={darkMode}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            
            <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}>
              <IconButton size="large" onClick={(e) => setAnchorElNav(e.currentTarget)} color="inherit">
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorElNav}
                open={Boolean(anchorElNav)}
                onClose={() => setAnchorElNav(null)}
                disableScrollLock
                TransitionComponent={Fade}
                slotProps={{ paper: dynamicMenuStyle }}
              >
                <MenuItem onClick={() => { navigate('/linklearning'); setAnchorElNav(null); }}>Explore</MenuItem>
                <MenuItem onClick={() => { navigate('/learning'); setAnchorElNav(null); }}>Learning</MenuItem>
                <MenuItem onClick={(e) => setAnchorElGames(e.currentTarget)}>Games »</MenuItem>
                <MenuItem onClick={(e) => setAnchorElApps(e.currentTarget)}>Websites and Apps »</MenuItem>
                {isAdmin && <MenuItem onClick={(e) => setAnchorElAdminSites(e.currentTarget)}>Admin »</MenuItem>}
              </Menu>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <ThemeToggle 
                checked={darkMode} 
                onChange={() => { toggleDarkMode(); sessionStorage.setItem('themeMode', darkMode ? 'light' : 'dark'); }} 
                icon={<LightModeIcon sx={{ fontSize: 18, color: "white" }} />}
                checkedIcon={<DarkModeIcon sx={{ fontSize: 16, color: "#fff" }} />}
              />
            </Box>

            <Typography variant="h6" noWrap component="a" onClick={() => navigate('/')}
              sx={{ flexGrow: { xs: 1, md: 0 }, mr: 2, display: 'flex', cursor: 'pointer' }}>
              <Box component="img" src={LLLogo} alt="Logo" sx={{ height: { xs: 40, md: 50 }, width: "auto" }} />
            </Typography>

            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              <Button sx={getButtonStyle('/linklearning')} onClick={() => navigate('/linklearning')}>Explore</Button>
              <Button sx={getButtonStyle('/learning')} onClick={() => navigate('/learning')}>Learning</Button>
              <Button sx={getButtonStyle('/games', isGamesActive())} onClick={(e) => setAnchorElGames(e.currentTarget)}>Games</Button>
              <Button sx={getButtonStyle('/apps', isAppsActive())} onClick={(e) => setAnchorElApps(e.currentTarget)}>Websites and Apps</Button>
              {isAdmin && <Button sx={getButtonStyle('/admin')} onClick={(e) => setAnchorElAdminSites(e.currentTarget)}>Admin</Button>}
            </Box>

            {/* Menü listák ugyanazok maradnak... */}
            <Menu 
              anchorEl={anchorElGames} open={Boolean(anchorElGames)} onClose={() => setAnchorElGames(null)}
              disableScrollLock TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
              anchorOrigin={{ vertical: isMobile ? 'center' : 'bottom', horizontal: isMobile ? 'right' : 'left' }}
            >
              <MenuItem onClick={() => { navigate('/click'); setAnchorElGames(null); setAnchorElNav(null); }}>Clicker</MenuItem>
              <MenuItem onClick={() => { navigate('/aimtester'); setAnchorElGames(null); setAnchorElNav(null); }}>Aim Tester</MenuItem>
              <MenuItem onClick={() => { navigate('/caseopening'); setAnchorElGames(null); setAnchorElNav(null); }}>Case Opening</MenuItem>
              <MenuItem onClick={() => { navigate('/catchme'); setAnchorElGames(null); setAnchorElNav(null); }}>Catch me</MenuItem>
              <MenuItem onClick={(e) => setAnchorElLeaderBoards(e.currentTarget)}><strong>Leaderboards »</strong></MenuItem>
            </Menu>

            <Menu 
              anchorEl={anchorElLeaderBoards} open={Boolean(anchorElLeaderBoards)} onClose={() => setAnchorElLeaderBoards(null)}
              disableScrollLock anchorOrigin={{ vertical: 'top', horizontal: 'right' }} 
              TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
            >
              <MenuItem onClick={() => { navigate('/leaderboard'); setAnchorElLeaderBoards(null); setAnchorElGames(null); setAnchorElNav(null); }}>Clicker Leaderboard</MenuItem>
              <MenuItem onClick={() => { navigate('/aimleaderboard'); setAnchorElLeaderBoards(null); setAnchorElGames(null); setAnchorElNav(null); }}>AIM Leaderboard</MenuItem>
            </Menu>

            <Menu 
              anchorEl={anchorElApps} open={Boolean(anchorElApps)} onClose={() => setAnchorElApps(null)}
              disableScrollLock TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
              anchorOrigin={{ vertical: isMobile ? 'center' : 'bottom', horizontal: isMobile ? 'right' : 'left' }}
            >
              <MenuItem onClick={() => { navigate('/encryption'); setAnchorElApps(null); setAnchorElNav(null); }}>Encryption</MenuItem>
              <MenuItem onClick={() => { navigate('/randomizer'); setAnchorElApps(null); setAnchorElNav(null); }}>Randomizer</MenuItem>
              <MenuItem onClick={() => { navigate('/pictureconverter'); setAnchorElApps(null); setAnchorElNav(null); }}>Picture Converter</MenuItem>
              <MenuItem onClick={() => { navigate('/valentincard'); setAnchorElApps(null); setAnchorElNav(null); }}>Valentin Card</MenuItem>
              <MenuItem onClick={(e) => setAnchorElWebsites(e.currentTarget)}><strong>Powerful Websites »</strong></MenuItem>
            </Menu>

            <Menu 
              anchorEl={anchorElWebsites} open={Boolean(anchorElWebsites)} onClose={() => setAnchorElWebsites(null)}
              disableScrollLock anchorOrigin={{ vertical: 'top', horizontal: 'right' }} 
              TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
            >
              <MenuItem onClick={() => { navigate('/freewebsites'); setAnchorElWebsites(null); setAnchorElApps(null); setAnchorElNav(null); }}>Free Websites</MenuItem>
              <MenuItem onClick={() => { navigate('/freetrialwebsites'); setAnchorElWebsites(null); setAnchorElApps(null); setAnchorElNav(null); }}>Free Trial Websites</MenuItem>
              <MenuItem onClick={() => { navigate('/paidwebsites'); setAnchorElWebsites(null); setAnchorElApps(null); setAnchorElNav(null); }}>Paid Websites</MenuItem>
            </Menu>

            {isAdmin && (
              <Menu 
                anchorEl={anchorElAdminSites} open={Boolean(anchorElAdminSites)} onClose={() => setAnchorElAdminSites(null)}
                disableScrollLock TransitionComponent={Fade} slotProps={{ paper: dynamicMenuStyle }}
              >
                <MenuItem onClick={() => { navigate('/admin'); setAnchorElAdminSites(null); setAnchorElNav(null); }}>Management</MenuItem>
                <MenuItem onClick={() => { navigate('/admincontent'); setAnchorElAdminSites(null); setAnchorElNav(null); }}>Content Authorization</MenuItem>
              </Menu>
            )}

            <Box sx={{ flexGrow: 0, ml: 1 }}>
              <IconButton onClick={(e) => setAnchorElUser(e.currentTarget)} sx={{ p: 0, border: '2px solid rgba(255,255,255,0.2)' }}>
                <Avatar alt="User Avatar" src={avatarUrl || DEFAULT_AVATAR} />
              </IconButton>
              <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={() => setAnchorElUser(null)}
                disableScrollLock
                TransitionComponent={Fade}
                slotProps={{ paper: dynamicMenuStyle }}
              >
                <MenuItem onClick={() => { navigate('/account'); setAnchorElUser(null); }}>Account</MenuItem>
                <MenuItem onClick={() => { navigate('/myscore'); setAnchorElUser(null); }}>My Score & Transfer</MenuItem>
                <MenuItem onClick={() => { navigate('/shop'); setAnchorElUser(null); }}>Shop🛒</MenuItem>
                <MenuItem onClick={() => { setLogoutDialogOpen(true); setAnchorElUser(null); }}>Logout</MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AnimatedAppBar>

      {/* --- AZ ÚJ ANIMÁLT LOGOUT DIALÓGUS --- */}
      <AnimatedDialog 
        open={logoutDialogOpen} 
        onClose={() => setLogoutDialogOpen(false)}
        disableScrollLock
        darkMode={darkMode}
        TransitionComponent={Fade}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2, 
              borderRadius: '12px',
              backgroundColor: darkMode ? 'rgba(255, 64, 129, 0.1)' : 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              '& .MuiAlert-icon': { color: darkMode ? '#ff4081' : 'white' }
            }}
          >
            All unsaved progress will be saved to your account.
          </Alert>
          <Typography variant="body1" sx={{ textAlign: "center", mb: 3, opacity: 0.9 }}>
            Are you sure you want to log out from your session?
          </Typography>
          
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ pb: 1 }}>
            <DialogActionButton
              darkMode={darkMode}
              onClick={() => setLogoutDialogOpen(false)}
            >
              Cancel
            </DialogActionButton>
            <DialogActionButton
              darkMode={darkMode}
              isLogout
              onClick={confirmLogout}
            >
              Yes, Log out
            </DialogActionButton>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button 
            onClick={() => setLogoutDialogOpen(false)} 
            sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontSize: '0.8rem' }}
          >
            Stay Logged In
          </Button>
        </DialogActions>
      </AnimatedDialog>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} message={notification?.message} />
      <Backdrop open={backdropOpen} sx={{ color: '#fff', zIndex: 2000 }}><CircularProgress color="inherit" /></Backdrop>
    </>
  );
}

export default DashBoardNavBar;